import 'whatwg-fetch';
import { varsFromServer } from '../client_side_injection_vars';

import algoliaClient from '../util/algolia_client';
import { getMapboxFeatureName } from './location_search_util';

export const MAPBOX_TOKEN = varsFromServer.mapboxToken;
export const MAPBOX_BASE_URL = 'https://api.mapbox.com';
export const MAPBOX_GEOCODING_ENDPOINT = '/geocoding/v5/mapbox.places/';
export const MAPBOX_BASE_PARAMS = { access_token: MAPBOX_TOKEN };

export const ALGOLIA_GEOGROUPING_INDEX = varsFromServer.algoliaGeogroupingIndex;
export const ALGOLIA_BUILDINGS_INDEX = varsFromServer.algoliaBuildingsIndex;
export const ALGOLIA_LOC_SEARCH_INDEX_NAMES = [ALGOLIA_GEOGROUPING_INDEX, ALGOLIA_BUILDINGS_INDEX];
export const ALGOLIA_HITS_PER_PAGE = 5;

export const SEARCH_RADIUS_IN_METERS = 600000;
export const FACET_FILTER_REMOVE_COUNTRYGEOS = ['type:-Countrygeo'];

export const ERROR_OUTDATED = 'Outdated response';

export async function performMapboxSearch({ query, locale = '' }) {
  const url = `${MAPBOX_BASE_URL}${MAPBOX_GEOCODING_ENDPOINT}${encodeURIComponent(query)}.json`;

  const endpointWithQueryParams = `${url}?access_token=${MAPBOX_TOKEN}&language=${locale}&limit=1`;

  const response = await fetch(endpointWithQueryParams);
  return response.json();
}

function buildBaseAlgoliaParams(index, locale = 'en-US') {
  if (index === ALGOLIA_GEOGROUPING_INDEX) {
    return {
      getRankingInfo: true,
      hitsPerPage: ALGOLIA_HITS_PER_PAGE,
      facetFilters: FACET_FILTER_REMOVE_COUNTRYGEOS,
      restrictSearchableAttributes: ['name', `name_${locale}`, 'parent.name', `parent.name_${locale}`],
      attributesToHighlight: ['name', `name_${locale}`, 'parent.name', `parent.name_${locale}`],
      attributesToRetrieve: ['id', 'slug', 'name', `name_${locale}`, 'parent.name', `parent.name_${locale}`]
    };
  }

  if (index === ALGOLIA_BUILDINGS_INDEX) {
    return {
      getRankingInfo: true,
      hitsPerPage: ALGOLIA_HITS_PER_PAGE,
      restrictSearchableAttributes: ['name', 'marketgeo.name', `marketgeo.name_${locale}`],
      attributesToHighlight: ['name', 'marketgeo.name', `marketgeo.name_${locale}`],
      attributesToRetrieve: ['id', 'slug', 'address', 'name', 'marketgeo.name', `marketgeo.name_${locale}`]
    };
  }

  return {};
}

/*
  response: {
    results: [
      // Geolocation Index
      { hits: [...] },
      // Building Index
      { hits: [...] },
    ]
  }
*/
export async function performAlgoliaExactSearch({ query, locale = 'en-US' }) {
  const queries = ALGOLIA_LOC_SEARCH_INDEX_NAMES.map(indexName => ({
    indexName,
    query,
    params: buildBaseAlgoliaParams(indexName, locale)
  }));

  return algoliaClient().search(queries);
}

/*
  response: {
    results: [
      // Geolocation Index
      { hits: [...] },
      // Building Index
      { hits: [...] },
    ]
  }
*/
export async function performAlgoliaGeoSearch({ longitude, latitude, locale = 'en-US' }) {
  const queries = ALGOLIA_LOC_SEARCH_INDEX_NAMES.map(indexName => ({
    indexName,
    query: '',
    params: {
      ...buildBaseAlgoliaParams(indexName, locale),
      aroundRadius: SEARCH_RADIUS_IN_METERS,
      aroundLatLng: [latitude, longitude]
    }
  }));

  return algoliaClient().search(queries);
}

export async function performAlgoliaNearbySearch(hitsPerPage = 5) {
  return algoliaClient().search([
    {
      indexName: ALGOLIA_GEOGROUPING_INDEX,
      query: '',
      params: {
        facetFilters: ['type:Marketgeo'],
        aroundLatLngViaIP: true,
        getRankingInfo: true,
        hitsPerPage
      }
    }
  ]);
}

// eslint-disable-next-line
export async function performComboSearch({ query, locale, validateLatestQuery }) {
  return Promise.all([performAlgoliaExactSearch({ query, locale }), performMapboxSearch({ query, locale })]).then(
    async ([exactResponse, mapboxSearchResponse]) => {
      if (!validateLatestQuery(query)) {
        return { canceled: true };
      }

      const exactHits = exactResponse;
      let geolocatedHits = [];

      const mapboxFeature =
        mapboxSearchResponse.features && mapboxSearchResponse.features.length > 0 && mapboxSearchResponse.features[0];
      let address;

      if (mapboxFeature) {
        const [longitude, latitude] = mapboxFeature.center;
        geolocatedHits = await performAlgoliaGeoSearch({ longitude, latitude, locale });

        if (!validateLatestQuery(query)) {
          return { canceled: true };
        }
        address = getMapboxFeatureName(mapboxFeature, locale);
      }

      return { exactHits, geolocatedHits, address };
    }
  );
}
